// Import react elements
import React, { useEffect, useRef } from "react";

// Import CSS
import "./RadialProgress.css";

/**
 * RadialProgress Component
 *
 * Renders a circular progress indicator using SVG. The progress can be
 * displayed clockwise or anticlockwise, with customizable size, colors,
 * and stroke width. The progress value is clamped between 0 and 100.
 *
 * Example usage:
 *
 * ```jsx
 * <RadialProgress
 *   value={75}
 *   direction="clockwise"
 *   size={150}
 *   strokeWidth={12}
 *   bgColor="#ccc"
 *   progressColor="#4caf50"
 * />
 * ```
 */


const RadialProgress = ({

  // Current progress value as a percentage (0 to 100)
  value = 0,

  // Direction of the progress animation: "clockwise" or "anticlockwise"
  direction = "clockwise",

  // Overall size of the progress circle in pixels
  size = 128,

  // Thickness of the circle stroke in pixels
  strokeWidth = 10,

  // Color for the background track of the progress circle
  bgColor = "#e5e7eb",

  // Color for the active progress portion of the circle
  progressColor = "#3b82f6"

}) => {
  
  // Reference to the foreground progress circle element in the SVG
  const circleRef = useRef(null);

  useEffect(() => {
    // Clamp the progress value to ensure it stays between 0 and 100
    const clamped = Math.max(0, Math.min(100, value));

    // Fixed radius of the progress circle
    const radius = 45;

    // Calculate the total circumference of the circle
    const circumference = 2 * Math.PI * radius;

    // Determine the stroke offset based on progress percentage
    const offset = circumference * (1 - clamped / 100);

    // Access the actual SVG circle element
    const circle = circleRef.current;

    // Set stroke dash properties to visualize progress
    circle.setAttribute("stroke-dasharray", circumference);
    circle.setAttribute("stroke-dashoffset", offset);

    // Flip horizontally if anticlockwise progress is desired
    const flip = direction === "anticlockwise" ? -1 : 1;
    circle.setAttribute("transform", `scale(${flip}, 1)`);

  }, [value, direction]);

  return (

    <div className="radial-progress" style={{ width: size, height: size }}>

      {/* SVG element to render the radial progress */}
      <svg viewBox="0 0 100 100" className="radial-progress-svg" >

        {/* Background circle track */}
        <circle className="radial-progress-bg" cx="50" cy="50" r="45" stroke={bgColor} strokeWidth={strokeWidth} />

        {/* Foreground progress circle */}
        <circle ref={circleRef} className="radial-progress-fg" cx="50" cy="50" r="45" stroke={progressColor} strokeWidth={strokeWidth} fill="none" />
      
      </svg>
     
    </div>

  );

};

export default RadialProgress;
